import apiClient from '../api/client';
import { API_BASE_URL } from '../config';

/**
 * Shared in-memory caches for data reused across modules (Data, Final, Rejected, etc.).
 * Deduplicates in-flight requests so parallel page mounts share one network call.
 */

const PUBLISHERS_TTL_MS = 15 * 60 * 1000; // 15 minutes
const CATEGORIES_TTL_MS = 30 * 60 * 1000; // 30 minutes
const USERS_TTL_MS = 10 * 60 * 1000; // 10 minutes

type CacheEntry<T> = {
  data: T;
  expiry: number;
};

let publishersCache: CacheEntry<any[]> | null = null;
let publishersInFlight: Promise<any[]> | null = null;

let categoriesCache: CacheEntry<Array<{ value: string; label: string }>> | null = null;
let categoriesInFlight: Promise<Array<{ value: string; label: string }>> | null = null;

let usersCache: CacheEntry<any[]> | null = null;
let usersInFlight: Promise<any[]> | null = null;

function isFresh<T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> {
  return !!entry && Date.now() < entry.expiry;
}

/** Main-tool publishers list (GET /publishers). Shared across Data / Final / Upload. */
export async function getCachedPublishers(forceRefresh = false): Promise<any[]> {
  if (!forceRefresh && isFresh(publishersCache)) {
    return publishersCache.data;
  }

  if (!forceRefresh && publishersInFlight) {
    return publishersInFlight;
  }

  publishersInFlight = (async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/publishers`);
      if (response.data.success) {
        const list = response.data.data || [];
        publishersCache = { data: list, expiry: Date.now() + PUBLISHERS_TTL_MS };
        return list;
      }
      return publishersCache?.data || [];
    } catch (err) {
      console.error('[appDataCache] Failed to fetch publishers:', err);
      return publishersCache?.data || [];
    } finally {
      publishersInFlight = null;
    }
  })();

  return publishersInFlight;
}

export function invalidatePublishersCache() {
  publishersCache = null;
  publishersInFlight = null;
}

/** Categories list (GET /categories). Shared across all data modules. */
export async function getCachedCategories(
  forceRefresh = false
): Promise<Array<{ value: string; label: string }>> {
  if (!forceRefresh && isFresh(categoriesCache)) {
    return categoriesCache.data;
  }

  if (!forceRefresh && categoriesInFlight) {
    return categoriesInFlight;
  }

  categoriesInFlight = (async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/categories`);
      if (response.data.success && Array.isArray(response.data.data)) {
        const list = response.data.data;
        categoriesCache = { data: list, expiry: Date.now() + CATEGORIES_TTL_MS };
        return list;
      }
      return categoriesCache?.data || [];
    } catch (err) {
      console.error('[appDataCache] Failed to fetch categories:', err);
      return categoriesCache?.data || [];
    } finally {
      categoriesInFlight = null;
    }
  })();

  return categoriesInFlight;
}

/** Users list (GET /users). Shared for assigned-to / uploader dropdowns. */
export async function getCachedUsers(forceRefresh = false): Promise<any[]> {
  if (!forceRefresh && isFresh(usersCache)) {
    return usersCache.data;
  }

  if (!forceRefresh && usersInFlight) {
    return usersInFlight;
  }

  usersInFlight = (async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users`);
      if (response.data.success) {
        const usersData = response.data.data;
        let usersList: any[] = [];
        if (usersData && Array.isArray(usersData.users)) {
          usersList = usersData.users;
        } else if (Array.isArray(usersData)) {
          usersList = usersData;
        }
        usersCache = { data: usersList, expiry: Date.now() + USERS_TTL_MS };
        return usersList;
      }
      return usersCache?.data || [];
    } catch (err) {
      console.error('[appDataCache] Failed to fetch users:', err);
      return usersCache?.data || [];
    } finally {
      usersInFlight = null;
    }
  })();

  return usersInFlight;
}

export function invalidateUsersCache() {
  usersCache = null;
  usersInFlight = null;
}

/** Clear all shared caches (e.g. on logout). */
export function clearAppDataCaches() {
  invalidatePublishersCache();
  categoriesCache = null;
  categoriesInFlight = null;
  invalidateUsersCache();
}
